import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

// Copyright 1999-2020. Plesk International GmbH. All rights reserved.

/* eslint-disable react/prop-types */
import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';
import { Z_INDEX_DROPDOWN, CLS_PREFIX } from '../../constants';
import classNames from 'classnames';
import Popper from '../Popper';
import Menu, { MenuItem, MenuHeader } from '../Menu';
import AutoClosable from '../AutoClosable';
import { safeInvoke, scrollIntoView } from '../utils';
export var findNextSafeIndex = function findNextSafeIndex(currIndex, diff, options) {
  var maxIndex = options.length - 1;
  var attempt = 0;

  for (var i = currIndex + diff; attempt < options.length; i += diff) {
    attempt += 1;

    if (i > maxIndex) {
      i = 0;
    } else if (i < 0) {
      i = maxIndex;
    }

    if (options[i] && options[i].disabled === false) {
      return i;
    }
  }

  return -1;
};

var ComboBoxDropdown = function ComboBoxDropdown(_ref) {
  var _ref$baseClassName = _ref.baseClassName,
      baseClassName = _ref$baseClassName === void 0 ? "".concat(CLS_PREFIX, "combobox-dropdown") : _ref$baseClassName,
      className = _ref.className,
      control = _ref.control,
      controlRef = _ref.controlRef,
      opened = _ref.opened,
      disabled = _ref.disabled,
      groups = _ref.groups,
      options = _ref.options,
      onHighlightedIndexChange = _ref.onHighlightedIndexChange,
      _onClose = _ref.onClose,
      value = _ref.value,
      highlightedIndex = _ref.highlightedIndex,
      onSelect = _ref.onSelect,
      noOptions = _ref.noOptions,
      emitter = _ref.emitter,
      props = _objectWithoutProperties(_ref, ["baseClassName", "className", "control", "controlRef", "opened", "disabled", "groups", "options", "onHighlightedIndexChange", "onClose", "value", "highlightedIndex", "onSelect", "noOptions", "emitter"]);

  var rootRef = useRef(null);
  var menuRef = useRef(null);

  var _useState = useState(0),
      _useState2 = _slicedToArray(_useState, 2),
      minWidth = _useState2[0],
      setMinWidth = _useState2[1];

  useLayoutEffect(function () {
    if (opened && rootRef.current) {
      var _rootRef$current$getB = rootRef.current.getBoundingClientRect(),
          width = _rootRef$current$getB.width;

      if (width !== minWidth) {
        setMinWidth(width);
      }
    }
  }, [opened, minWidth]);
  useEffect(function () {
    if (!opened) {
      return undefined;
    }

    var listener = function listener() {
      safeInvoke(_onClose);
    };

    window.addEventListener('resize', listener, {
      once: true
    });
    return function () {
      return window.removeEventListener('resize', listener);
    };
  }, [opened, _onClose]);
  useEffect(function () {
    if (!emitter) {
      return;
    }

    emitter.on('focus', function (index) {
      if (menuRef.current) {
        var _target = menuRef.current.querySelector("[data-index=\"".concat(index, "\"]"));

        if (_target) {
          scrollIntoView(_target, {
            behavior: 'instant',
            block: 'nearest',
            boundary: menuRef.current
          });
        }
      }
    });
  }, [emitter]);
  var target = React.createElement(AutoClosable, {
    onClose: function onClose() {
      setTimeout(function () {
        safeInvoke(_onClose);
      }, 0);
    },
    ignoreNodes: function ignoreNodes() {
      return [menuRef.current];
    },
    nodeRef: controlRef
  }, control);
  return React.createElement("div", _extends({
    className: className
  }, props, {
    ref: rootRef
  }), React.createElement(Popper, {
    target: target,
    targetRef: controlRef,
    show: opened && !disabled,
    className: "".concat(baseClassName, "__popper"),
    zIndex: Z_INDEX_DROPDOWN
  }, React.createElement(Menu, {
    style: {
      minWidth: minWidth
    },
    ref: menuRef,
    className: "".concat(baseClassName, "__menu")
  }, groups.reduce(function (acc, group) {
    if (group.label) {
      acc.push(React.createElement(MenuHeader, _extends({}, group.props, {
        key: "group_".concat(group.label)
      }), group.label));
    }

    group.options.forEach(function (option) {
      var optionIndex = options.indexOf(option);
      var onClick;
      var onMouseOver;
      var onMouseDown;

      if (!option.disabled) {
        onClick = function onClick(e) {
          e.preventDefault();
          safeInvoke(onSelect, option.value);
        };

        onMouseOver = function onMouseOver() {
          // TODO: use throttle
          safeInvoke(onHighlightedIndexChange, optionIndex);
        };

        onMouseDown = function onMouseDown(e) {
          e.preventDefault();
        };
      }

      var menuItemProps = _objectSpread({}, option.props, {
        active: value === option.value,
        className: classNames("".concat(baseClassName, "__option"), _defineProperty({}, "".concat(baseClassName, "__option--selected"), optionIndex === highlightedIndex)),
        disabled: option.disabled,
        onClick: onClick,
        onMouseOver: onMouseOver,
        onMouseDown: onMouseDown,
        'data-index': optionIndex
      });

      acc.push(React.createElement(MenuItem, _extends({
        key: "option_".concat(option.value)
      }, menuItemProps), option.children));
    });
    return acc;
  }, []), options.length === 0 && noOptions && React.createElement(MenuItem, {
    disabled: true,
    className: "".concat(baseClassName, "__no-options")
  }, noOptions))));
};

export default ComboBoxDropdown;